<template>
  <div class="grid grid-cols-3 gap-2">
    <l-selectable-button
      v-for="(option, index) in mostProbableHandedAmounts"
      :key="option + index"
      type="vertical"
      :is-selected="chargeAmount !== 0 && (selectedAmount === option || chargeAmount === option)"
      @click="$emit('amountSelected', option)"
    >
      <template v-if="mode === 'tip'">
        <div>{{ index === 0 ? t('checkout.no-tip') : `${TIP_OPTIONS[index]}%` }}</div>
      </template>
      <template v-else>
        {{ currency(option) }}
      </template>
      <template v-if="mode === 'tip' && index != 0" #description>
        {{ currency(tipAmounts[index]) }}
      </template>
    </l-selectable-button>

    <l-selectable-button
      type="vertical"
      :is-selected="!!selectedAmount && !mostProbableHandedAmounts.includes(selectedAmount)"
      @click="showCustomAmount = true"
    >
      {{ t('checkout.custom') }}
      <template
        #description
        v-if="selectedAmount && !mostProbableHandedAmounts.includes(selectedAmount)"
      >
        {{ mode == 'tip' ? currency(selectedAmount - (amount ?? 0)) : currency(selectedAmount) }}
      </template>
    </l-selectable-button>
    <custom-amount-component
      v-model:open="showCustomAmount"
      :description="
        mode === 'tip'
          ? t('checkout.custom-tip-description')
          : t('checkout.custom-amount-description')
      "
      :default-amount="chargeAmount ?? 0"
      @new-amount="onCustomAmount"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useMoney } from '@/composables/useMoney'

import CustomAmountComponent from './CustomAmount.vue'
import LSelectableButton from './LSelectableButton.vue'

interface Props {
  mode: 'change' | 'tip'
  amount?: number
  selectedAmount?: number
  chargeAmount?: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  amountSelected: [number]
}>()

const { currency } = useMoney()
const { t } = useI18n()

const showCustomAmount = ref(false)

const TIP_OPTIONS = [0, 5, 10, 12.5, 15]

// For displaying tip amounts in UI (tip only)
const tipAmounts = computed(() => {
  if (props.mode === 'tip') {
    const baseAmount = props.amount ?? 0
    return TIP_OPTIONS.map(percentage => Math.round(baseAmount * (percentage / 100)))
  }
  return []
})

function onCustomAmount(amount: number) {
  const customAmount = props.mode === 'tip' ? amount + (props.amount ?? 0) : amount
  emit('amountSelected', customAmount)
  showCustomAmount.value = false
}

const mostProbableHandedAmounts = computed(() => {
  if (props.mode === 'tip') {
    const baseAmount = props.amount ?? 0
    return TIP_OPTIONS.map(percentage => Math.round(baseAmount * (1 + percentage / 100)))
  }

  const priceInCents = props.amount ?? 0
  const denominations = [5000, 2000, 1000, 500] // En céntimos: 50€, 20€, 10€, 5€

  function getCombination(amountInCents: number) {
    const combination = []
    for (const bill of denominations) {
      while (amountInCents >= bill) {
        amountInCents -= bill
        combination.push(bill)
      }
    }
    return combination
  }

  function calculateScore(combination: number[], extraMoneyInCents: number): number {
    return combination.length * 10 + extraMoneyInCents / 100
  }

  const roundedPriceInCents = Math.ceil(priceInCents / 100) * 100 // Redondear al euro superior
  const nextHigherAmountInCents = Math.ceil(priceInCents / 500) * 500 // Redondear a los 5€ superiores

  let possiblePayments = []

  if (roundedPriceInCents > priceInCents && roundedPriceInCents <= priceInCents + 5000) {
    const combination = getCombination(roundedPriceInCents)
    const extraMoney = roundedPriceInCents - priceInCents
    const score = calculateScore(combination, extraMoney) - 100
    possiblePayments.push({ amount: roundedPriceInCents, combination, extraMoney, score })
  }

  for (let amount = nextHigherAmountInCents; amount <= priceInCents + 5000; amount += 500) {
    if (amount === priceInCents || amount === roundedPriceInCents) continue
    const combination = getCombination(amount)
    const extraMoney = amount - priceInCents
    const score = calculateScore(combination, extraMoney)
    possiblePayments.push({ amount, combination, extraMoney, score })
  }

  possiblePayments = possiblePayments.sort((a, b) => a.score - b.score).slice(0, 4)

  possiblePayments.sort((a, b) => a.amount - b.amount)

  return [priceInCents, ...possiblePayments.map(item => item.amount)]
})
</script>
